import type { OnChanges, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  inject,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Clipboard } from '@freelancer/clipboard';
import { ButtonComponent } from '@freelancer/ui/button';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { IconModule } from '@freelancer/ui/icon';
import { HoverColor, IconColor, IconSize } from '@freelancer/ui/icon';
import { FontWeight } from '@freelancer/ui/text';
import { ToastAlertService, ToastAlertType } from '@freelancer/ui/toast-alert';
import {
  SocialButtonColor,
  SocialButtonSize,
  SocialButtonType,
  SocialUrls,
} from './social-buttons.types';

@Component({
  standalone: true,
  selector: 'fl-social-buttons',
  imports: [ButtonComponent, IconModule, MatTooltipModule],
  template: `
    @if (shareUrl || socialUrls.facebook) {
      <div
        class="SocialButtonItem mr-xxxsmall"
        role="listitem"
      >
        <fl-button
          class="SocialButton SocialButton--facebook"
          [attr.data-type]="type"
          [attr.data-theme]="color"
          [link]="shareUrl ? shareUrls['facebook'] : socialUrls.facebook"
          [color]="shareUrl ? ButtonColor.CUSTOM : undefined"
          [size]="shareUrl ? ButtonSize.MINI : undefined"
          (click)="handleClick('facebook')"
        >
          @if (shareUrl) {
            <fl-icon
              label="Share on Facebook"
              i18n-label="Share link on Facebook social button"
              [color]="IconColor.INHERIT"
              [hoverColor]="HoverColor.CURRENT"
              [name]="'ui-facebook'"
              [size]="size"
              [sizeTablet]="sizeTablet"
              [sizeDesktop]="sizeDesktop"
            ></fl-icon>
          }
          @if (!shareUrl) {
            <fl-icon
              label="Facebook"
              i18n-label="Facebook social button"
              [color]="IconColor.INHERIT"
              [hoverColor]="HoverColor.CURRENT"
              [name]="'ui-facebook'"
              [size]="size"
              [sizeTablet]="sizeTablet"
              [sizeDesktop]="sizeDesktop"
            ></fl-icon>
          }
        </fl-button>
      </div>
    }
    @if (shareUrl || socialUrls.twitter) {
      <div
        class="SocialButtonItem mr-xxxsmall"
        role="listitem"
      >
        <fl-button
          class="SocialButton SocialButton--x"
          [attr.data-type]="type"
          [attr.data-theme]="color"
          [link]="shareUrl ? shareUrls['twitter'] : socialUrls.twitter"
          [color]="shareUrl ? ButtonColor.CUSTOM : undefined"
          [size]="shareUrl ? ButtonSize.MINI : undefined"
          (click)="handleClick('twitter')"
        >
          @if (shareUrl) {
            <fl-icon
              label="Share on X"
              i18n-label="Share link on X social button"
              [color]="IconColor.INHERIT"
              [hoverColor]="HoverColor.CURRENT"
              [name]="'ui-x'"
              [size]="size"
              [sizeTablet]="sizeTablet"
              [sizeDesktop]="sizeDesktop"
            ></fl-icon>
          }
          @if (!shareUrl) {
            <fl-icon
              label="X"
              i18n-label="X social button"
              [color]="
                color == SocialButtonColor.LIGHT
                  ? IconColor.LIGHT
                  : IconColor.INHERIT
              "
              [hoverColor]="
                color == SocialButtonColor.LIGHT
                  ? HoverColor.LIGHT
                  : HoverColor.CURRENT
              "
              [name]="'ui-x'"
              [size]="size"
              [sizeTablet]="sizeTablet"
              [sizeDesktop]="sizeDesktop"
            ></fl-icon>
          }
        </fl-button>
      </div>
    }
    @if (!shareUrl && socialUrls.youtube) {
      <div
        class="SocialButtonItem mr-xxxsmall"
        role="listitem"
      >
        <fl-button
          class="SocialButton SocialButton--youtube"
          [attr.data-type]="type"
          [attr.data-theme]="color"
          [link]="socialUrls.youtube"
          (click)="handleClick('youtube')"
        >
          <fl-icon
            label="YouTube"
            i18n-label="YouTube social button"
            [color]="IconColor.INHERIT"
            [hoverColor]="HoverColor.CURRENT"
            [name]="'ui-youtube'"
            [size]="size"
            [sizeTablet]="sizeTablet"
            [sizeDesktop]="sizeDesktop"
          ></fl-icon>
        </fl-button>
      </div>
    }
    @if (!shareUrl && socialUrls.instagram) {
      <div
        class="SocialButtonItem mr-xxxsmall"
        role="listitem"
      >
        <fl-button
          class="SocialButton SocialButton--instagram"
          [attr.data-type]="type"
          [attr.data-theme]="color"
          [link]="socialUrls.instagram"
          (click)="handleClick('instagram')"
        >
          <fl-icon
            label="Instagram"
            i18n-label="Instagram social button"
            [color]="IconColor.INHERIT"
            [hoverColor]="HoverColor.CURRENT"
            [name]="'ui-instagram'"
            [size]="size"
            [sizeTablet]="sizeTablet"
            [sizeDesktop]="sizeDesktop"
          ></fl-icon>
        </fl-button>
      </div>
    }
    @if (shareUrl || socialUrls.linkedin) {
      <div
        class="SocialButtonItem mr-xxxsmall"
        role="listitem"
      >
        <fl-button
          class="SocialButton SocialButton--linkedin"
          [attr.data-type]="type"
          [attr.data-theme]="color"
          [link]="shareUrl ? shareUrls['linkedin'] : socialUrls.linkedin"
          [color]="shareUrl ? ButtonColor.CUSTOM : undefined"
          [size]="shareUrl ? ButtonSize.MINI : undefined"
          (click)="handleClick('linkedin')"
        >
          @if (shareUrl) {
            <fl-icon
              label="Share on LinkedIn"
              i18n-label="Share link on LinkedIn social button"
              [color]="IconColor.INHERIT"
              [hoverColor]="HoverColor.CURRENT"
              [name]="'ui-linkedin'"
              [size]="size"
              [sizeTablet]="sizeTablet"
              [sizeDesktop]="sizeDesktop"
            ></fl-icon>
          }
          @if (!shareUrl) {
            <fl-icon
              label="LinkedIn"
              i18n-label="LinkedIn social button"
              [color]="IconColor.INHERIT"
              [hoverColor]="HoverColor.CURRENT"
              [name]="'ui-linkedin'"
              [size]="size"
              [sizeTablet]="sizeTablet"
              [sizeDesktop]="sizeDesktop"
            ></fl-icon>
          }
        </fl-button>
      </div>
    }
    @if (shareUrl) {
      <div
        class="SocialButtonItem mr-xxxsmall"
        role="listitem"
      >
        <fl-button
          class="SocialButton SocialButton--pinterest"
          [attr.data-type]="type"
          [attr.data-theme]="color"
          [link]="shareUrls['pinterest']"
          [color]="ButtonColor.CUSTOM"
          [size]="ButtonSize.MINI"
          (click)="handleClick('pinterest')"
        >
          <fl-icon
            label="Share on Pinterest"
            i18n-label="Share link on Pinterest social button"
            [color]="IconColor.INHERIT"
            [hoverColor]="HoverColor.CURRENT"
            [name]="'ui-pinterest'"
            [size]="size"
            [sizeTablet]="sizeTablet"
            [sizeDesktop]="sizeDesktop"
          ></fl-icon>
        </fl-button>
      </div>
    }
    @if (shareUrl) {
      <div
        class="SocialButtonItem mr-xxxsmall"
        role="listitem"
      >
        <fl-button
          class="SocialButton SocialButton--copyLink"
          i18n-matTooltip="Copy to clipboard tooltip message"
          matTooltip="Copy Link"
          [attr.data-type]="type"
          [attr.data-theme]="color"
          [color]="ButtonColor.CUSTOM"
          [size]="ButtonSize.MINI"
          (click)="copyToClipboard(shareUrl || '')"
        >
          <fl-icon
            label="Copy link to clipboard"
            i18n-label="Copy link to clipboard social button"
            [color]="IconColor.INHERIT"
            [hoverColor]="HoverColor.CURRENT"
            [name]="'ui-link-v2'"
            [size]="size"
            [sizeTablet]="sizeTablet"
            [sizeDesktop]="sizeDesktop"
            [useIconFont]="true"
            [fillIconFont]="true"
            [weight]="FontWeight.BOLD"
          ></fl-icon>
        </fl-button>
      </div>
    }
  `,
  styleUrls: ['./social-buttons.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SocialButtonsComponent implements OnChanges, OnInit {
  private toastAlertService = inject(ToastAlertService);
  private clipboard = inject(Clipboard);

  SocialButtonColor = SocialButtonColor;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  IconColor = IconColor;
  FontWeight = FontWeight;
  HoverColor = HoverColor;

  shareUrls: { [key: string]: string } = {};
  DEFAULT_SOCIAL_URLS: SocialUrls = {
    facebook: 'https://facebook.com/fansoffreelancer',
    twitter: 'https://x.com/freelancer',
    youtube: 'https://youtube.com/freelancerchannel',
    instagram: 'https://instagram.com/freelancerofficial',
  };
  socialUrls: SocialUrls = this.DEFAULT_SOCIAL_URLS;

  @Input() color: SocialButtonColor = SocialButtonColor.FOREGROUND;
  @Input() shareUrl?: string;
  @Input() shareText?: string;
  @Input() size: SocialButtonSize = IconSize.MID;
  @Input() sizeTablet?: SocialButtonSize;
  @Input() sizeDesktop?: SocialButtonSize;

  @Input() set customSocialUrls(value: SocialUrls) {
    this.socialUrls = value || this.DEFAULT_SOCIAL_URLS;
  }

  /** Maximize the width of the parent container */
  @HostBinding('attr.data-fluid')
  @Input()
  fluid?: boolean;

  @HostBinding('attr.data-type')
  type: SocialButtonType;

  @HostBinding('attr.role')
  role = 'list';

  ngOnChanges(): void {
    this.setType();
    this.createShareUrls();
  }

  ngOnInit(): void {
    this.setType();
  }

  setType(): void {
    this.type = this.shareUrl ? SocialButtonType.BUTTON : SocialButtonType.ICON;
  }

  createShareUrls(): void {
    const text = (this.shareText || '').split(' ').join('+');

    if (!this.shareUrl) {
      return;
    }

    this.shareUrls.facebook = `http://www.facebook.com/share.php?u=${this.shareUrl}`;
    this.shareUrls.twitter = `https://x.com/intent/tweet?text=${text}: ${this.shareUrl} via @freelancer Community`;
    this.shareUrls.linkedin = `http://www.linkedin.com/shareArticle?mini=true&url=${this.shareUrl}&title=${text}&source=https://www.freelancer.com/community`;
    this.shareUrls.pinterest = `https://pinterest.com/pin/create/bookmarklet/?url=${this.shareUrl}`;
    this.shareUrls.copyLink = this.shareUrl;
  }

  copyToClipboard(url: string): void {
    if (this.clipboard.copy(url)) {
      this.toastAlertService.open({
        message: $localize`Copied to clipboard.`,
        type: ToastAlertType.SUCCESS,
      });
    } else {
      this.toastAlertService.open({
        message: $localize`Copy to clipboard failed, please try again after adjusting your
      permissions.`,
        type: ToastAlertType.ERROR,
      });
    }
  }

  // Tracking could be added here later if required.
  handleClick(platform: string): void {
    // Empty
  }
}
